'use client';

import { useState, useEffect } from 'react';
import { cn } from '@bazaar-marketplace/utils';
import { useTranslations } from 'next-intl';
import { Typography } from '~/components/typography';
import { useLatestValueRef } from '~/lib/use-latest-value-ref';
import debug from 'debug';

interface CountdownTimerProps {
  targetDate: Date;
  className?: string;
  segmentWidth?: number;
  onTimerEnd?: () => void;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const logger = debug('app:countdown-timer');

export const CountdownTimer = ({ targetDate, className, segmentWidth, onTimerEnd }: CountdownTimerProps) => {
  const t = useTranslations('join-to-get-goodies.countdown');

  const calculateTimeLeft = (): TimeLeft => {
    const now = new Date().getTime();
    const difference = targetDate.getTime() - now;

    if (difference > 0) {
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    }
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  };

  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() => calculateTimeLeft());

  const latestRef = useLatestValueRef({ onTimerEnd });
  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);

      if (newTimeLeft.days === 0 && newTimeLeft.hours === 0 && newTimeLeft.minutes === 0 && newTimeLeft.seconds === 0) {
        clearInterval(timer);
        if (latestRef.current.onTimerEnd) {
          logger('Timer ended.');
          latestRef.current.onTimerEnd();
        }
      }
    }, 1000);

    return () => {
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetDate, latestRef]);

  function renderTimeSegment(data: { value: string; label: string }) {
    return (
      <div className="flex flex-col gap-1 items-center text-center w-20 rounded-2xl" style={{ width: segmentWidth }}>
        <Typography className="text-[24px] text-white flex items-center" weight="bold">
          <span>{data.value.padStart(2, '0')}</span>
        </Typography>
        <Typography className="text-[12px] text-white/40" weight="bold">
          {data.label}
        </Typography>
      </div>
    );
  }

  function renderSeparator() {
    return <div className="border border-white/10 h-20"></div>;
  }

  return (
    <div className={cn('flex text-white font-bold text-2xl tracking-wide items-center', className)}>
      {}
      {renderTimeSegment({
        value: String(timeLeft.days),
        label: t('days'),
      })}

      {renderSeparator()}

      {renderTimeSegment({
        value: String(timeLeft.hours),
        label: t('hours'),
      })}

      {renderSeparator()}

      {renderTimeSegment({
        value: String(timeLeft.minutes),
        label: t('minutes'),
      })}

      {renderSeparator()}

      {renderTimeSegment({
        value: String(timeLeft.seconds),
        label: t('seconds'),
      })}
    </div>
  );
};
